import { PrimaryButtonMT } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import SearchBarInput from '@/components/form-inputs/SearchBarInput';
import { Table } from '@/components/table/Tables';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Toolbar } from 'primereact/toolbar';
import { useState } from 'react';
import { MdPersonAddAlt1 } from 'react-icons/md';
import AddGroupScreen from '../../views/AddGroupScreen';
import { useQuery } from '@tanstack/react-query';
import { GroupListItem } from '../../engine/group.types';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse, ListRequest, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { useTableState } from '@/common/useTableState';
import { X4bUserRoleEnum } from '@/constants/roles';
import { useAuthenticationStore } from '@/store';

const GroupTable = () => {
	const navigate = useNavigate();
	const [openAddModal, setOpenAddModal] = useState(false);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);

	const { sort, filter, updateFilter, tableProps } = useTableState({ skip: 0, take: 5, nameFilter: '' });

	const { data: groups } = useQuery({
		queryKey: ['GroupTable.GetGroups', sort, filter],
		initialData: { Items: [] as GroupListItem[], TotalRecords: 0 },
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<GroupListItem>>>(ENDPOINTS['/api/X4bGroup/GetGroups'], {
				SortField: sort.sortField ?? "",
				SortOrder: sort.sortOrder,
				Skip: filter.skip,
				Take: filter.take,
				Filters: [{ Name: "Name", Value: filter.nameFilter }]
			} as ListRequest);
			return result.data.Data;
		}
	});

	const startContent = (
		<div className="flex">
			<SearchBarInput
				placeholder="Search by name"
				onChange={(e) => updateFilter({ nameFilter: e.target.value })}
				className="w-96"
			/>
		</div>
	);

	const endContent = (
		<div className="flex">
			<PrimaryButtonMT
				id="add-group"
				label="Create Group"
				icon={<MdPersonAddAlt1 className="mr-2" />}
				onClick={() => setOpenAddModal(true)}
			/>
		</div>
	);

	return (
		<div>
			<Toolbar
				pt={{
					root: {
						className: 'people-toolbar mb-6',
					},
				}}
				start={startContent}
				end={isManagerOrAdmin ? endContent : (<></>)}
			/>
			<Table
				value={groups.Items}
				emptyMessage={EmptyTable({
					title: 'No groups yet',
					description: 'You’ll able to see your groups here.',
					src: '/images/svg/Empty State - No Employees.svg',
				})}
				onRowClick={(e) => {
					const group = e.data as GroupListItem;
					navigate(`/people/group/${group.Id}`);
				}}
				lazy
				paginator={groups.Items.length > 0}
				totalRecords={groups.TotalRecords}

				removableSort={true}
				rowsPerPageOptions={[5, 10, 25, 50]}
				{...tableProps}
			>
				<Column field="Name" header="Group Name" sortable
					body={(rowData) => (
						<div>
							<p className="font-semibold">{rowData.Name}</p>
							<p className="text-gray-500 text-xs">{rowData.Description}</p>
						</div>
					)}
				/>
				<Column field="PeopleCount" header="Number of People" sortable />
				<Column field="Program" header="Program" sortable />
			</Table>
			<AddGroupScreen
				visible={openAddModal}
				onHide={() => { setOpenAddModal(false); }}
			/>
		</div>
	);
};

export default GroupTable;
